import { ForwardedRef, forwardRef } from "react";
import {
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  ModalBody,
  VStack,
  HStack
} from "@gluestack-ui/themed";

import { Icon } from "@repo/core/components/core/Icon";

import { Modal } from "../../Modal";
import { IModalRefProps } from "../../Modal/types";
import { AlertModalProps } from "./types";
import { ActionSheet } from "../../Actionsheet";
import { AlertModalFooter } from "./components/AlertModalFooter";
import { AlertModalBody } from "./components/AlertModalBody";
import { useAlertModalController } from "./controller";
import { Text } from "@repo/core/components/core/Text";

export const AlertModal = forwardRef(function Drawer(
  {
    title,
    text,
    options,
    variant,
    icon,
    headerTitle,
    headerIcon,
    description,
    iconColor,
    iconVariant,
    maxWidth,
    isLoading,
    showIcon,
    children
  }: AlertModalProps,
  ref: ForwardedRef<IModalRefProps>
) {
  const { isMobile, style } = useAlertModalController({ variant });

  const renderMobile = () => {
    return (
      <ActionSheet ref={ref}>
        <VStack w="$full" px="$2">
          {headerTitle ? (
            <>
              <HStack alignItems="center" p="$2">
                {headerIcon ? (
                  <VStack mr="$2">
                    <Icon name={headerIcon} size={14} />
                  </VStack>
                ) : null}
                <Text fontSize="$md" color="$textLight900" fontWeight={600}>
                  {headerTitle}
                </Text>
              </HStack>
              {description ? (
                <Text
                  fontSize="$sm"
                  color="$backgroundLight700"
                  fontWeight={400}
                >
                  {description}
                </Text>
              ) : null}
            </>
          ) : null}
          <AlertModalBody
            title={title}
            text={text}
            icon={style?.icon || icon}
            iconColor={style?.iconColor || iconColor}
            iconVariant={style?.iconVariant || iconVariant}
            lottieFileConfig={style?.lottieFileConfig}
            isLoading={isLoading}
            showIcon={showIcon}
          >
            {children}
          </AlertModalBody>
          {options &&
          Array.isArray(options) &&
          options.length > 0 &&
          !isLoading ? (
            <AlertModalFooter options={options} />
          ) : null}
        </VStack>
      </ActionSheet>
    );
  };

  const renderDesktop = () => {
    return (
      <Modal ref={ref} maxWidth={maxWidth}>
        <ModalHeader justifyContent="flex-end">
          {headerTitle ? (
            <>
              <Text
                fontFamily="Roboto"
                fontWeight={700}
                fontSize="$lg"
                color="$backgroundLight900"
                ml="$2"
                flex={1}
              >
                {headerTitle}
              </Text>
            </>
          ) : null}
          <ModalCloseButton>
            <Icon name="times" size="xs" />
          </ModalCloseButton>
        </ModalHeader>
        {description ? (
          <VStack px="$6">
            <Text fontSize="$sm" color="$backgroundLight700" fontWeight={400}>
              {description}
            </Text>
          </VStack>
        ) : null}
        <ModalBody>
          <AlertModalBody
            title={title}
            text={text}
            icon={style?.icon || icon}
            iconVariant={style?.iconVariant || iconVariant}
            iconColor={style?.iconColor || iconColor}
            lottieFileConfig={style?.lottieFileConfig}
            isLoading={isLoading}
            showIcon={showIcon}
          >
            {children}
          </AlertModalBody>
        </ModalBody>
        {options && Array.isArray(options) && options.length > 0 ? (
          <ModalFooter>
            {!isLoading && <AlertModalFooter options={options} />}
          </ModalFooter>
        ) : null}
      </Modal>
    );
  };

  return isMobile ? renderMobile() : renderDesktop();
});
